// Plan Inferno API client — per-engine spatial-allocation results.
//
// Backed by `GET /api/v1/projects/{project_id}/runs/{run_id}/inferno-result`,
// which returns one row per (run_id, engine) from `project_inferno_results`.
// Each engine's `payload` shape varies — see InMAP/EASIUR/USEtox/LC-IMPACT
// engines under `packages/model/src/totalview_model/inferno/engines/`.

import { fetchWithAuth } from "./http";

/**
 * One per-engine row from the inferno-result endpoint.
 *
 * Mirrors `InfernoEngineResultOut` in
 * `packages/schemas/src/totalview_schemas/api/model_runs.py`. The
 * payload is intentionally typed as `Record<string, any>` because the
 * shape is engine-specific and evolves as new engines come online.
 */
export interface InfernoEngineResult {
  engine: string;
  payload: Record<string, any> | null;
  error: string | null;
  n_plants_total: number | null;
  n_plants_with_emissions: number | null;
  n_plants_with_county_fips: number | null;
  created_at: string;
}

/**
 * Fetch all persisted inferno engine results for a single (project, run).
 *
 * Returns an empty list (not an error) for runs that completed before
 * Plan Inferno was enabled, or for runs whose engine bootstraps all
 * failed gracefully. 404s when the run doesn't exist or the caller can't
 * see it (admin-on-other-customer with no `customer_id` plumbing falls
 * into this case for V1).
 */
export async function getInfernoResult(
  projectId: string,
  runId: string,
  token: string,
): Promise<InfernoEngineResult[]> {
  return fetchWithAuth<InfernoEngineResult[]>(
    `/projects/${projectId}/runs/${runId}/inferno-result`,
    token,
  );
}
